import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import AdminDeleteButton from "@/components/AdminDeleteButton";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { Loader2, ArrowLeft, ChevronRight, CheckCircle2, XCircle, RefreshCw, FileText, ExternalLink, Download } from "lucide-react";
import LineItemsEditor from "@/components/LineItemsEditor";
import AuditTrail from "@/components/AuditTrail";
import CommercialStatusBadge from "@/components/CommercialStatusBadge";
import CommercialChain from "@/components/CommercialChain";
import SendEmailDialog from "@/components/SendEmailDialog";
import { Mail } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

const EMAIL_ROLES = new Set(["admin", "super_admin", "plant_head", "sales"]);

export default function QuotationDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);
  const [reviseDialog, setReviseDialog] = useState(false);
  const [rejectDialog, setRejectDialog] = useState(false);
  const [emailDialog, setEmailDialog] = useState(false);
  const [reviseRemarks, setReviseRemarks] = useState("");
  const [revisedLines, setRevisedLines] = useState([]);
  const [rejectReason, setRejectReason] = useState("");
  const [saving, setSaving] = useState(false);

  async function refresh() {
    setLoading(true);
    const { data } = await api.get(`/quotations/${id}`);
    setD(data);
    setLoading(false);
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [id]);

  if (loading || !d) {
    return <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-blue-900" /></div>;
  }

  const canApprove = d.status === "Draft" && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);
  const canRevise = ["Draft", "Approved"].includes(d.status) && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);
  const canReject = d.status === "Draft" && ["plant_head", "admin", "super_admin"].includes(user.role);
  const canConvertToPo = d.status === "Approved" && ["sales", "plant_head", "admin", "super_admin"].includes(user.role);

  function openRevise() {
    setRevisedLines(d.line_items.map((li) => ({ ...li })));
    setReviseRemarks("");
    setReviseDialog(true);
  }

  async function approve() {
    setSaving(true);
    try {
      await api.post(`/quotations/${id}/approve`);
      toast.success("Quotation approved");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function revise() {
    if (!reviseRemarks.trim()) { toast.error("Revision remarks required"); return; }
    setSaving(true);
    try {
      await api.post(`/quotations/${id}/revise`, {
        revision_remarks: reviseRemarks,
        line_items: revisedLines,
        terms: d.terms,
      });
      toast.success(`Revision ${d.current_revision + 1} created`);
      setReviseDialog(false);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  async function reject() {
    if (!rejectReason.trim()) { toast.error("Reason required"); return; }
    setSaving(true);
    try {
      await api.post(`/quotations/${id}/reject`, { reason: rejectReason });
      toast.success("Quotation rejected");
      setRejectDialog(false); setRejectReason("");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSaving(false); }
  }

  return (
    <div data-testid="quotation-detail-page" className="space-y-5 pb-6">
      <div className="flex items-center gap-2 text-xs text-slate-500">
        <Link to="/quotations" className="hover:text-slate-900 flex items-center gap-1">
          <ArrowLeft className="w-3 h-3" /> Quotations
        </Link>
        <ChevronRight className="w-3 h-3" />
        <span className="font-mono text-slate-900">{d.quotation_number}</span>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold">
              Quotation · {d.quotation_type === "M" ? "Manufacturing" : d.quotation_type === "L" ? "Reconditioning" : d.quotation_type === "B" ? "Budgetary" : "Quotation"} · Rev {d.current_revision}
            </div>
            <div className="mt-1 font-mono text-xl md:text-2xl font-bold text-slate-900" data-testid="qtn-display-number">
              {d.display_number || d.quotation_number}
            </div>
            {d.base_quotation_number && d.display_number && d.base_quotation_number !== d.display_number && (
              <div className="text-[11px] font-mono text-slate-500">
                Base: {d.base_quotation_number}
              </div>
            )}
            <div className="mt-2 flex items-center gap-3 text-sm flex-wrap">
              <span className="text-slate-600">{d.customer_name} · {d.plant_name}</span>
              <span className="text-slate-300">·</span>
              <span className="text-slate-500 font-mono text-xs">{d.quotation_date}</span>
              {d.inquiry_number && (
                <>
                  <span className="text-slate-300">·</span>
                  <Link to={`/inquiries/${d.inquiry_id}`} className="text-blue-900 hover:underline text-xs font-mono">
                    ↑ {d.inquiry_number}
                  </Link>
                </>
              )}
              {d.delivery_challan_number && (
                <>
                  <span className="text-slate-300">·</span>
                  {d.delivery_challan_id ? (
                    <Link to={`/receiving/${d.delivery_challan_id}`}
                          data-testid="qtn-dc-link"
                          className="text-amber-800 hover:underline text-xs font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      DC {d.delivery_challan_number}
                    </Link>
                  ) : (
                    <span data-testid="qtn-dc-badge" className="text-amber-800 text-xs font-mono bg-amber-50 border border-amber-200 rounded px-1.5 py-0.5">
                      DC {d.delivery_challan_number}
                    </span>
                  )}
                </>
              )}
            </div>
          </div>
          <div className="flex flex-col md:items-end gap-3">
            <div className="flex items-center gap-3">
              <CommercialStatusBadge status={d.status} className="text-xs px-3 py-1" />
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">Total Value</div>
                <div className="font-mono text-lg font-bold text-slate-900">₹{(d.total_value || 0).toLocaleString("en-IN")}</div>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" data-testid="qtn-export-btn"
                      onClick={async () => {
                        try {
                          const resp = await api.get(`/quotations/${id}/export`, { responseType: "blob" });
                          const url = URL.createObjectURL(new Blob([resp.data]));
                          const a = document.createElement("a"); a.href = url;
                          a.download = `Quotation_${(d.display_number || d.quotation_number).replace(/\//g, "_")}.xlsx`;
                          document.body.appendChild(a); a.click(); a.remove();
                          URL.revokeObjectURL(url);
                        } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Export failed"); }
                      }}>
                <Download className="w-3.5 h-3.5 mr-1" /> Export Excel
              </Button>
              {EMAIL_ROLES.has(user?.role) && (
                <Button size="sm" variant="outline" data-testid="qtn-send-email-btn"
                        onClick={() => setEmailDialog(true)}>
                  <Mail className="w-3.5 h-3.5 mr-1" /> Send Email
                </Button>
              )}
              {canApprove && (
                <Button size="sm" data-testid="qtn-approve-btn" onClick={approve} disabled={saving}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white">
                  <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                </Button>
              )}
              {canRevise && (
                <Button size="sm" variant="outline" data-testid="qtn-revise-btn" onClick={openRevise}>
                  <RefreshCw className="w-3.5 h-3.5 mr-1" /> Revise
                </Button>
              )}
              {canReject && (
                <Button size="sm" variant="outline" data-testid="qtn-reject-btn"
                        onClick={() => setRejectDialog(true)}
                        className="border-red-200 text-red-700 hover:bg-red-50">
                  <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                </Button>
              )}
              {canConvertToPo && (
                <Button size="sm" data-testid="qtn-to-po-btn"
                        onClick={() => nav(`/purchase-orders/new?quotation_id=${id}`)}
                        className="bg-blue-900 hover:bg-blue-800 text-white">
                  <FileText className="w-3.5 h-3.5 mr-1" /> Record Customer PO
                </Button>
              )}
              <AdminDeleteButton
                endpoint={`/quotations/${id}`}
                entityName="Quotation"
                entityNumber={d.display_number || d.quotation_number}
                redirectAfter="/quotations"
              />
            </div>
          </div>
        </div>
      </Card>

      <Card className="border-slate-200 p-4">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">Commercial Chain</div>
        <CommercialChain type="quotation" entity={d} />
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <Card className="border-slate-200 lg:col-span-2 p-5">
          <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Line Items · Rev {d.current_revision}</div>
          <LineItemsEditor value={d.line_items || []} onChange={() => {}} readOnly showPrice />
          {d.terms && (
            <div className="mt-4 pt-4 border-t border-slate-100">
              <div className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">Terms</div>
              <p className="text-xs text-slate-700 whitespace-pre-line">{d.terms}</p>
            </div>
          )}
        </Card>

        <div className="space-y-5">
          <Card className="border-slate-200 p-5" data-testid="revision-history-card">
            <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Revision History</div>
            <div className="space-y-2">
              {(d.revisions || []).map((r) => (
                <div key={r.id} data-testid={`rev-${r.revision_number}`}
                     className={`p-2 border rounded ${r.is_current ? "border-blue-400 bg-blue-50/50" : "border-slate-200"}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-xs font-bold text-slate-800">
                        {r.revision_label || `Rev ${r.revision_number}`}
                      </span>
                      {r.is_current ? (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-blue-100 text-blue-800 border border-blue-200 px-1.5 py-0.5 rounded">Current</span>
                      ) : (
                        <span className="text-[9px] font-bold uppercase tracking-widest bg-slate-100 text-slate-600 border border-slate-200 px-1.5 py-0.5 rounded">History</span>
                      )}
                    </div>
                    <span className="text-xs font-mono text-slate-500">₹{(r.total_value || 0).toLocaleString("en-IN")}</span>
                  </div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {r.revision_date} · {r.created_by_name}
                  </div>
                  <div className="text-xs text-slate-600 italic mt-0.5">&quot;{r.remarks}&quot;</div>
                </div>
              ))}
            </div>
          </Card>

          {/* Commercial Terms card (always shown; empty state is OK) */}
          <Card className="border-slate-200 p-5" data-testid="commercial-terms-card">
            <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Commercial Terms</div>
            <dl className="grid grid-cols-1 gap-2 text-xs">
              {[
                ["Price Basis", d.commercial_terms?.price_basis],
                ["Freight", d.commercial_terms?.freight],
                ["Packing & Forwarding", d.commercial_terms?.packing_forwarding],
                ["Payment Terms", d.commercial_terms?.payment_terms || d.terms],
                ["Delivery Date", d.commercial_terms?.delivery_date],
                ["Offer Validity", d.commercial_terms?.offer_validity || `${d.validity_days} days (until ${d.valid_until})`],
                ["GST Applicable", d.commercial_terms?.gst_applicable],
                ["Drawing Approval Required", d.commercial_terms?.drawing_approval_required],
                ["Warranty", d.commercial_terms?.warranty],
              ].map(([label, val]) => (
                <div key={label} className="flex justify-between gap-2 border-b border-slate-100 pb-1 last:border-0">
                  <dt className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold">{label}</dt>
                  <dd className="text-slate-800 text-right">{val || <span className="text-slate-300">—</span>}</dd>
                </div>
              ))}
            </dl>
          </Card>

          {d.purchase_orders?.length > 0 && (
            <Card className="border-slate-200 p-5">
              <div className="text-sm font-semibold text-slate-900 mb-3" style={{ fontFamily: "Chivo, sans-serif" }}>Linked Customer POs</div>
              {d.purchase_orders.map((po) => (
                <Link key={po.id} to={`/purchase-orders/${po.id}`}
                      className="flex items-center justify-between p-2 border border-slate-200 rounded hover:border-blue-400 hover:bg-blue-50/40">
                  <div>
                    <div className="font-mono text-xs font-semibold text-blue-900">{po.po_number}</div>
                    <div className="text-[11px] text-slate-500">Customer PO: {po.customer_po_reference}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <CommercialStatusBadge status={po.status} />
                    <ExternalLink className="w-3.5 h-3.5 text-slate-400" />
                  </div>
                </Link>
              ))}
            </Card>
          )}
        </div>
      </div>

      <Card className="border-slate-200 p-5">
        <div className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: "Chivo, sans-serif" }}>Audit Trail</div>
        <AuditTrail items={d.audit_trail || []} />
      </Card>

      {/* Revise dialog */}
      <Dialog open={reviseDialog} onOpenChange={setReviseDialog}>
        <DialogContent data-testid="revise-dialog" className="max-w-5xl">
          <DialogHeader>
            <DialogTitle>Create Revision {d.current_revision + 1}</DialogTitle>
            <DialogDescription>Modify line items and unit prices, then save the new revision.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1">
            <div>
              <Label className="text-xs uppercase tracking-wider text-slate-600">Revision Remarks *</Label>
              <Textarea data-testid="revise-remarks" value={reviseRemarks} rows={2}
                        onChange={(e) => setReviseRemarks(e.target.value)} className="mt-1"
                        placeholder="e.g. Price reduction after negotiation" />
            </div>
            <LineItemsEditor value={revisedLines} onChange={setRevisedLines} showPrice />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviseDialog(false)}>Cancel</Button>
            <Button data-testid="revise-confirm" onClick={revise} disabled={saving} className="bg-blue-900 hover:bg-blue-800">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : `Save Rev ${d.current_revision + 1}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject dialog */}
      <Dialog open={rejectDialog} onOpenChange={setRejectDialog}>
        <DialogContent data-testid="reject-dialog">
          <DialogHeader>
            <DialogTitle>Reject Quotation</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs uppercase tracking-wider text-slate-600">Reason *</Label>
            <Textarea data-testid="reject-reason" value={rejectReason} rows={3}
                      onChange={(e) => setRejectReason(e.target.value)} className="mt-1" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRejectDialog(false)}>Cancel</Button>
            <Button data-testid="reject-confirm" onClick={reject} disabled={saving} className="bg-red-600 hover:bg-red-700">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Reject"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SendEmailDialog
        open={emailDialog} onClose={() => setEmailDialog(false)}
        defaultTo=""
        defaultSubject={d ? `Quotation ${d.display_number || d.quotation_number} — ${d.customer_name}` : ""}
        defaultBody={d ? `Dear Sir/Madam,\n\nPlease find attached our quotation ${d.display_number || d.quotation_number} for your kind reference.\n\nBest regards,\nHISPL Sales Team` : ""}
        relatedModule="quotation" relatedRecordId={id}
        relatedRecordNumber={d?.display_number || d?.quotation_number}
        attachmentEndpoint={`/quotations/${id}/export`}
        attachmentFilename={d ? `Quotation_${(d.display_number || d.quotation_number).replace(/\//g, "_")}.xlsx` : "Quotation.xlsx"}
      />
    </div>
  );
}
